import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';

import { api } from '../lib/api';

export interface Me {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  membershipStatus: string;
  duesStatus: string;
  duesPaidUntil: string | null;
  smsEnabled: boolean;
  emailEnabled: boolean;
  smsConsentAt: string | null;
  emailOptOut: boolean;
  org: {
    id: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    primaryColor: string | null;
    accentColor: string | null;
    timezone: string;
  };
}

interface Value {
  me: Me | null;
  loading: boolean;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<Value | null>(null);

async function fetchMe(): Promise<Me | null> {
  try {
    const { data } = await api.get<Me>('/public/me');
    return data;
  } catch {
    return null;
  }
}

export function MemberAuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isPending, refetch } = useQuery({
    queryKey: ['public-me'],
    queryFn: fetchMe,
    staleTime: 30_000,
    retry: false,
  });

  const logout = useCallback(async () => {
    try {
      await api.post('/public/auth/logout');
    } finally {
      qc.clear();
      window.location.href = '/';
    }
  }, [qc]);

  const value = useMemo<Value>(
    () => ({
      me: data ?? null,
      loading: isPending,
      refetch: async () => {
        await refetch();
      },
      logout,
    }),
    [data, isPending, logout, refetch],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMemberAuth(): Value {
  const v = useContext(Ctx);
  if (!v) throw new Error('useMemberAuth outside MemberAuthProvider');
  return v;
}
